import { useEffect, useState } from "react";
import { useAsync } from "../hooks/useAsync";
import {
  addKettlebell,
  deleteKettlebell,
  exportData,
  getDeletionStatus,
  getPreferences,
  listKettlebells,
  requestDeletion,
  cancelDeletion,
  updatePreferences,
  updateProfile,
} from "../api/endpoints";
import {
  EXPERIENCE_LEVELS,
  EXPERIENCE_LEVEL_LABELS,
  type ExperienceLevel,
  type Preferences,
} from "../api/types";
import { Button, Card, ErrorMessage, PageHeading, Spinner } from "../components/ui";
import { useAuth } from "../hooks/useAuth";

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none dark:border-[#2e2e2e] dark:bg-[#111111] dark:text-gray-100";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="mb-4 p-5">
      <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {children}
    </Card>
  );
}

function ProfileSection() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // Seed from the loaded profile. `user` is null on first render while the
  // profile is still in flight, so this cannot be initial state.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName ?? "");
    setWeightUnit(user.weightUnit);
  }, [user]);

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await updateProfile({ displayName: displayName || null, weightUnit });
      await refreshUser();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Profile">
      <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Display name
      </label>
      <input
        id="displayName"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className={inputClass}
        data-testid="settings-input-displayname"
      />

      <label htmlFor="weightUnit" className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Weight unit
      </label>
      <select
        id="weightUnit"
        value={weightUnit}
        onChange={(e) => setWeightUnit(e.target.value as "kg" | "lb")}
        className={inputClass}
        data-testid="settings-select-weightunit"
      >
        <option value="kg">Kilograms</option>
        <option value="lb">Pounds</option>
      </select>

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => void save()} disabled={busy} data-testid="settings-button-saveprofile">
          Save profile
        </Button>
        {saved && (
          <span className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="settings-profile-saved">
            Saved
          </span>
        )}
      </div>
    </Section>
  );
}

function PreferencesSection() {
  const { data, error, loading, reload } = useAsync(getPreferences, []);
  const [draft, setDraft] = useState<Preferences | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  if (loading) return <Section title="Training preferences"><Spinner /></Section>;
  if (error)
    return (
      <Section title="Training preferences">
        <ErrorMessage error={error} onRetry={reload} />
      </Section>
    );
  if (!draft) return null;

  const set = <K extends keyof Preferences>(key: K, value: Preferences[K]) =>
    setDraft({ ...draft, [key]: value });

  async function save() {
    if (!draft) return;
    setBusy(true);
    setSaved(false);
    try {
      await updatePreferences(draft);
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Training preferences">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="experience" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Experience level
          </label>
          <select
            id="experience"
            value={draft.experienceLevel}
            onChange={(e) => set("experienceLevel", e.target.value as ExperienceLevel)}
            className={inputClass}
            data-testid="settings-select-experience"
          >
            {EXPERIENCE_LEVELS.map((l) => (
              <option key={l} value={l}>
                {EXPERIENCE_LEVEL_LABELS[l]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Caps how complex an exercise the planner will show you.
          </p>
        </div>

        <div>
          <label htmlFor="minutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Preferred session length (minutes)
          </label>
          <input
            id="minutes"
            type="number"
            min={5}
            max={180}
            value={draft.preferredSessionMinutes}
            onChange={(e) => set("preferredSessionMinutes", Number(e.target.value))}
            className={inputClass}
            data-testid="settings-input-minutes"
          />
        </div>

        <div>
          <label htmlFor="rounds" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Default rounds
          </label>
          <input
            id="rounds"
            type="number"
            min={1}
            max={20}
            value={draft.defaultRounds}
            onChange={(e) => set("defaultRounds", Number(e.target.value))}
            className={inputClass}
            data-testid="settings-input-rounds"
          />
        </div>

        <div>
          <label htmlFor="restSets" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Rest between sets (seconds)
          </label>
          <input
            id="restSets"
            type="number"
            min={0}
            max={600}
            value={draft.restBetweenSetsSeconds}
            onChange={(e) => set("restBetweenSetsSeconds", Number(e.target.value))}
            className={inputClass}
            data-testid="settings-input-restsets"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => void save()} disabled={busy} data-testid="settings-button-savepreferences">
          Save preferences
        </Button>
        {saved && (
          <span className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="settings-preferences-saved">
            Saved
          </span>
        )}
      </div>
    </Section>
  );
}

function KettlebellsSection() {
  const { data, error, loading, reload } = useAsync(listKettlebells, []);
  const [weight, setWeight] = useState("16");
  const [quantity, setQuantity] = useState("1");
  const [busy, setBusy] = useState(false);

  const items = data?.items ?? [];

  async function add() {
    setBusy(true);
    try {
      await addKettlebell(Number(weight), Number(quantity));
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await deleteKettlebell(id);
    reload();
  }

  return (
    <Section title="Your kettlebells">
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-500">
        The planner only prescribes weights you actually own.
      </p>

      {loading && <Spinner />}
      {error && <ErrorMessage error={error} onRetry={reload} />}

      {items.length > 0 && (
        <ul className="mb-4 divide-y divide-gray-100 dark:divide-[#2e2e2e]">
          {items.map((kb) => (
            <li key={kb.id} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-gray-900 dark:text-gray-100">
                {kb.weightKg} kg{kb.quantity > 1 ? ` × ${kb.quantity}` : ""}
              </span>
              <button
                onClick={() => void remove(kb.id)}
                className="text-sm font-medium text-amber-700 hover:underline dark:text-amber-400"
                data-testid={`settings-kettlebell-remove-${kb.id}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {data && items.length === 0 && (
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400" data-testid="settings-kettlebells-empty">
          No kettlebells added yet.
        </p>
      )}

      <div className="flex items-end gap-2">
        <div className="w-28">
          <label htmlFor="kbWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Weight (kg)
          </label>
          <input
            id="kbWeight"
            type="number"
            min={1}
            max={100}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className={inputClass}
            data-testid="settings-input-kbweight"
          />
        </div>
        <div className="w-24">
          <label htmlFor="kbQty" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Quantity
          </label>
          <input
            id="kbQty"
            type="number"
            min={1}
            max={10}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className={inputClass}
            data-testid="settings-input-kbquantity"
          />
        </div>
        <Button onClick={() => void add()} disabled={busy} data-testid="settings-button-addkettlebell">
          Add
        </Button>
      </div>
    </Section>
  );
}

function DataSection() {
  const { data, reload } = useAsync(getDeletionStatus, []);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function download() {
    setBusy(true);
    setMessage("");
    try {
      const payload = await exportData();
      // Built client-side: the API returns JSON rather than a stored file, so
      // there is no URL to link to.
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "loadandflow-export.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  // The API reports a pending deletion as a non-null deletionRequestedAt;
  // there is no boolean flag.
  const pendingDeletion = data?.deletionRequestedAt != null;

  async function toggleDeletion() {
    setBusy(true);
    setMessage("");
    try {
      if (pendingDeletion) await cancelDeletion();
      else await requestDeletion();
      reload();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Your data">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => void download()} disabled={busy} data-testid="settings-button-export">
          Download my data
        </Button>
        <Button
          variant="danger"
          onClick={() => void toggleDeletion()}
          disabled={busy}
          data-testid="settings-button-deletion"
        >
          {pendingDeletion ? "Cancel account deletion" : "Delete my account"}
        </Button>
      </div>

      {pendingDeletion && (
        <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" data-testid="settings-deletion-pending">
          Your account is scheduled for deletion
          {data?.scheduledPurgeAt
            ? ` after ${new Date(data.scheduledPurgeAt).toLocaleDateString()}`
            : ""}
          . You
          can still cancel until then.
        </p>
      )}

      {message && (
        <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" data-testid="settings-data-message">
          {message}
        </p>
      )}
    </Section>
  );
}

export default function SettingsPage() {
  return (
    <div data-testid="settings-page">
      <PageHeading title="Settings" />
      <ProfileSection />
      <PreferencesSection />
      <KettlebellsSection />
      <DataSection />
    </div>
  );
}
